import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ComponentPreview from './ComponentPreview';
import Button from '../atoms/Button';
import Text from '../atoms/Text';
import Badge from '../atoms/Badge';

/**
 * Code preview panel with fullscreen toggle and metadata
 */
const CodePreviewPanel = ({ 
  htmlCode,
  cssCode,
  jsCode,
  title = 'Component Preview',
  className = ''
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Calculate code statistics
  const stats = {
    html: htmlCode ? { lines: htmlCode.split('\n').length, chars: htmlCode.length } : { lines: 0, chars: 0 },
    css: cssCode ? { lines: cssCode.split('\n').length, chars: cssCode.length } : { lines: 0, chars: 0 },
    js: jsCode ? { lines: jsCode.split('\n').length, chars: jsCode.length } : { lines: 0, chars: 0 }
  };

  const totalLines = stats.html.lines + stats.css.lines + stats.js.lines;
  const totalChars = stats.html.chars + stats.css.chars + stats.js.chars;

  return (
    <div className={`bg-white border border-gray-200 rounded-lg overflow-hidden ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-3">
          <Text variant="h4" className="text-gray-900">
            {title}
          </Text>
          {totalLines > 0 && (
            <div className="flex gap-2">
              <Badge variant="default" size="sm">
                {totalLines} lines
              </Badge>
              <Badge variant="purple" size="sm">
                {totalChars} chars
              </Badge>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="default"
            size="sm"
            onClick={toggleFullscreen}
            leftIcon={
              isFullscreen ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
              )
            }
          >
            {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
          </Button>
        </div>
      </div>

      {/* Preview Area */}
      <div className={`relative ${isFullscreen ? 'fixed inset-0 z-50 bg-white' : ''}`}>
        {isFullscreen && (
          <div className="absolute top-4 right-4 z-10">
            <Button
              variant="default"
              size="sm"
              onClick={toggleFullscreen}
              leftIcon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              }
            >
              Exit Fullscreen
            </Button>
          </div>
        )}
        
        <div className={`${isFullscreen ? 'h-screen p-4' : 'h-96'}`}>
          <ComponentPreview
            htmlCode={htmlCode}
            cssCode={cssCode}
            jsCode={jsCode}
          />
        </div>
      </div>

      {/* Code Statistics Footer */}
      {!isFullscreen && (
        <div className="px-4 py-2 bg-gray-50 border-t border-gray-200">
          <div className="flex justify-between text-xs text-gray-500">
            <div className="flex gap-4">
              {stats.html.lines > 0 && <span>HTML: {stats.html.lines} lines</span>}
              {stats.css.lines > 0 && <span>CSS: {stats.css.lines} lines</span>}
              {stats.js.lines > 0 && <span>JS: {stats.js.lines} lines</span>}
            </div>
            <div>
              Last updated: {new Date().toLocaleTimeString()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

CodePreviewPanel.propTypes = {
  htmlCode: PropTypes.string,
  cssCode: PropTypes.string,
  jsCode: PropTypes.string,
  title: PropTypes.string,
  className: PropTypes.string
};

export default CodePreviewPanel;
